import { DatePipe } from '@angular/common';
import { Component, EventEmitter, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import {
  CellClickedEvent,
  ColDef,
  ColumnMovedEvent,
  ColumnResizedEvent,
  ColumnVisibleEvent,
  GridReadyEvent,
  ICellRendererParams,
  RowSelectedEvent,
  SortChangedEvent,
} from 'ag-grid-community';
import { catchError, debounceTime, throwError } from 'rxjs';
import { AGTableBase } from '../../../shared/components/table/ag-table-base';
import { SipConfig, sipConfigToUpdate } from '../../../shared/models/sip.model';
import { LocalStorageService } from '../../../shared/services/localStorage.service';
import { ModalService } from '../../../shared/services/modal.service';
import { NotificationService } from '../../../shared/services/notification.service';
import { SipConfigRequestParams, SipService } from '../../../shared/services/sip.service';
import { UsersService } from '../../../shared/services/users.service';
import { SipConfigTableAction, SipConfigTableActionsComponent } from './sip-config-table-actions/sip-config-table-actions.component';

@Component({
  standalone: false,
  selector: 'app-sip-config-table',
  templateUrl: './sip-config-table.component.html',
})
export class SipConfigTableComponent extends AGTableBase implements OnInit {
  @Output() actions = new EventEmitter<SipConfigTableAction>();
  @Output() onAfterDelete = new EventEmitter<void>();
  @Output() changeBatch = new EventEmitter<SipConfig[]>();

  @ViewChild('deleteModalTpl', { read: TemplateRef, static: false }) deleteModalTpl: any;

  loading = true;
  batchSelectedIds = new Set<number>();

  // ng grid
  columnDefs: ColDef<SipConfig>[] = [
    {
      headerName: '',
      field: 'batch',
      checkboxSelection: true,
      maxWidth: 40,
      headerCheckboxSelection: true,
      pinned: 'left',
      lockPinned: true,
      lockPosition: 'left',
      lockVisible: true,
      suppressColumnsToolPanel: true,
      suppressMenu: true,
      suppressMovable: true,
      cellClass: 'batch-cell',
    },
    {
      headerName: 'ID',
      field: 'id',
      maxWidth: 80,
      minWidth: 80,
      sortable: true,
      initialSort: 'desc',
      sortingOrder: ['desc', 'asc', null],
      lockVisible: true,
      cellRenderer: (params: ICellRendererParams<SipConfig>) => {
        const _ = params.data;
        return `<div class="one-line">${_.id}</div>`;
      },
    },
    {
      headerName: 'Created',
      field: 'createdAt',
      sortable: true,
      comparator: () => 0,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      cellRenderer: (params: ICellRendererParams<SipConfig>) => {
        const _ = params.data;
        return `<div class="one-line">${this.formatDate(_.createdAt)}</div>`;
      },
    },
    {
      headerName: 'Updated',
      field: 'updatedAt',
      sortable: true,
      comparator: () => 0,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      initialHide: true,
      cellRenderer: (params: ICellRendererParams<SipConfig>) => {
        const _ = params.data;
        return `<div class="one-line">${_.updatedAt ? this.formatDate(_.updatedAt) : ''}</div>`;
      },
    },
    {
      headerName: 'Name',
      field: 'name',
      sortable: true,
      comparator: () => 0,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      headerTooltip: 'An identifier for your SIP configuration. Helps distinguish between multiple trunks you have.',
      cellRenderer: (params: ICellRendererParams<SipConfig>) => {
        const _ = params.data;
        return `<div class="one-line">${_.name}</div>`;
      },
    },
    {
      headerName: 'Protocol',
      field: 'protocol',
      sortable: false,
      comparator: () => 0,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      headerTooltip: 'Select the protocol used by your SIP server (currently only UDP protocol is supported).',
      cellRenderer: (params: ICellRendererParams<SipConfig>) => {
        const _ = params.data;
        return `<div class="one-line">${_.protocol}</div>`;
      },
    },
    {
      headerName: 'Host',
      field: 'host',
      sortable: true,
      comparator: () => 0,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      headerTooltip: 'The IP address or domain name of your SIP server and the port on which your SIP server is listening (e.g., 5060). ',
      cellRenderer: (params: ICellRendererParams<SipConfig>) => {
        const { online, pingEnabled, host, port, enabled } = params.data;
        const cellTitle = online ? 'Connected' : !pingEnabled && enabled ? 'No Connection. OPTIONS pings are disabled.' : 'No Connection';
        const statusClass = online ? 'text-success-s2' : !pingEnabled && enabled ? 'text-warning-w2' : 'text-error-r2';

        return `<div class="one-line d-flex align-items-center gap-0_5" title="${cellTitle}" >
                    ${host}:${port} <span class="fas fa-circle ${statusClass}"></span>
                </div>`;
      },
    },
    {
      headerName: 'Prefix',
      field: 'prefix',
      sortable: true,
      comparator: () => 0,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      initialHide: true,
      headerTooltip:
        'A numeric string automatically added before the CLI when sending test calls through this SIP trunk. <br> Use it only if your SIP server requires specific number formatting (e.g., adding 00 before international numbers).',
      cellRenderer: (params: ICellRendererParams<SipConfig>) => {
        const _ = params.data;
        return `<div class="one-line">${_.prefix ? _.prefix : 'N/A'}</div>`;
      },
    },
    {
      headerName: 'Throughput (Call/second)',
      field: 'cps',
      sortable: false,
      comparator: () => 0,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      initialHide: true,
      headerTooltip: 'The rate at which your SIP server can send calls. ',
      cellRenderer: (params: ICellRendererParams<SipConfig>) => {
        const _ = params.data;
        return `<div class="one-line">${_.cps ? _.cps : 'N/A'}</div>`;
      },
    },
    {
      headerName: 'Enabled',
      field: 'enabled',
      minWidth: 110,
      maxWidth: 110,
      sortable: false,
      comparator: () => 0,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      cellClass: 'justify-content-center',
      cellRenderer: (params: ICellRendererParams<SipConfig>) => {
        const isChecked = params.data.enabled ? 'checked' : '';
        return `<div class="form-check form-switch d-flex justify-content-center">
                            <input type="checkbox" class="form-check-input medium" ${isChecked}/>
                        </div>`;
      },
      onCellClicked: (event: CellClickedEvent<SipConfig>) => this.onClickEnabled(event.data),
    },
    {
      headerName: 'Ping Enabled',
      field: 'pingEnabled',
      minWidth: 120,
      maxWidth: 120,
      initialHide: true,
      sortable: false,
      comparator: () => 0,
      initialSort: null,
      sortingOrder: ['desc', 'asc', null],
      cellClass: 'justify-content-center',
      cellRenderer: (params: ICellRendererParams<SipConfig>) => {
        const isChecked = params.data.pingEnabled ? 'checked' : '';
        return `<div class="form-check form-switch d-flex justify-content-center">
                            <input type="checkbox" class="form-check-input medium" ${isChecked}/>
                        </div>`;
      },
      onCellClicked: (event: CellClickedEvent<SipConfig>) => this.onClickPingEnabled(event.data),
    },
    {
      headerName: 'User',
      field: 'user.email',
      sortable: false,
      comparator: () => 0,
      initialHide: false,
      cellRenderer: (params: ICellRendererParams<SipConfig>) => {
        const _ = params.data;
        return `<div class="one-line">${_.user ? _.user.email : 'N/A'}</div>`;
      },
    },
    {
      headerName: 'Actions',
      field: 'id',
      maxWidth: 100,
      minWidth: 100,
      pinned: 'right',
      lockPinned: true,
      lockPosition: 'right',
      lockVisible: true,
      suppressColumnsToolPanel: false,
      suppressMenu: false,
      suppressAutoSize: true,
      headerTooltip: 'Right click on a row to see more options.',
      headerClass: 'action-cell',
      cellClass: 'action-cell',
      cellRenderer: SipConfigTableActionsComponent,
      cellRendererParams: {
        onAction: (action: SipConfigTableAction) => this.onAction(action),
      },
    },
  ];

  rowData!: SipConfig[];
  // ng grid

  search = '';

  constructor(
    public notificationService: NotificationService,
    public service: SipService,
    public userService: UsersService,
    public modal: ModalService,
    private datePipe: DatePipe,
    private localStorage: LocalStorageService
  ) {
    super();
  }

  ngOnInit() {
    this.loading = true;
    this.paginationPageSize = this.localStorage.get(`sip_config_table_size_${this.userService.authUser.id}`) || 20;
    this.columnChange$.pipe(debounceTime(1000)).subscribe((event: ColumnMovedEvent | ColumnResizedEvent | ColumnVisibleEvent) => {
      const currentColumnState = this.gridApi.getColumnState();
      this.localStorage.set(`sip_config_table_state_${this.userService.authUser.id}`, currentColumnState);
    });
  }

  changeSize($event, size) {
    this.paginationPageSize = size;
    this.localStorage.set(`sip_config_table_size_${this.userService.authUser.id}`, size);
    this.update();
  }

  onSortChange(event: SortChangedEvent) {
    super.onSortChange(event);
    const currentColumnState = this.gridApi.getColumnState();
    this.localStorage.set(`sip_config_table_state_${this.userService.authUser.id}`, currentColumnState);
  }

  onAction(action: SipConfigTableAction) {
    if (action.name === 'delete') {
      const dialogRef = this.modal.alert().size('sm').component(this.deleteModalTpl).open();
      dialogRef.result.then(result => {
        if (result) {
          this.delete(action.data);
        }
      });
      return;
    }
    this.actions.emit(action);
  }

  update() {
    this.loading = true;
    const params = new SipConfigRequestParams();
    params.page = this.currentPageNumber - 1;
    params.size = this.paginationPageSize;
    params.search = this.search.trim();
    this.sortState
      .filter(_ => !!_)
      .forEach(sort => {
        const [rawField, order] = sort.split(',');
        let field = rawField;
        if (field === 'createdAt') {
          field = 'created_at';
        }
        params.setSort(field, order);
      });
    this.service
      .allConfig(params)
      .pipe(
        catchError(e => {
          this.loading = false;
          this.notificationService.error({
            title: 'SIP config',
            message: 'An error occurred while loading the configuration',
            serviceName: 'SIP',
            requestMessage: e.statusText,
            requestCode: e.status,
          });
          return throwError(() => e);
        })
      )
      .subscribe(data => {
        this.rowData = data.content;
        this.totalRowsCount = data.totalElements;
        this.loading = false;
      });
  }

  showNewest() {
    this.currentPageNumber = 1;
    this.sortState = ['id,desc'];
    if (this.gridApi) {
      this.gridApi.applyColumnState({
        defaultState: { sort: null },
        state: [{ colId: 'id', sort: 'desc' }],
      });
    }
    this.update();
  }

  delete(report: SipConfig) {
    this.loading = true;
    this.service
      .deleteConfig(report.id)
      .pipe(
        catchError(e => {
          this.loading = false;
          this.notificationService.error({
            title: 'SIP config',
            message: 'An error occurred while deleting the config',
            serviceName: 'SIP',
            requestMessage: e.statusText,
            requestCode: e.status,
          });
          return throwError(() => e);
        })
      )
      .subscribe(() => {
        this.notificationService.success('Config removed', 'SIP config');
        this.update();
      });
  }

  onClickEnabled(config: SipConfig) {
    this.loading = true;
    config.enabled = !config.enabled;
    this.service
      .saveConfig(sipConfigToUpdate(config))
      .pipe(
        catchError(e => {
          this.loading = false;
          this.notificationService.error({
            title: 'SIP config',
            message: 'An error occurred while saving the config',
            serviceName: 'SIP',
            requestMessage: e.statusText,
            requestCode: e.status,
          });
          config.enabled = !config.enabled;
          return throwError(() => e);
        })
      )
      .subscribe(() => {
        this.loading = false;
        this.notificationService.success('Config saved', 'SIP config');
        this.update();
      });
  }

  onClickPingEnabled(config: SipConfig) {
    this.loading = true;
    config.pingEnabled = !config.pingEnabled;
    this.service
      .saveConfig(sipConfigToUpdate(config))
      .pipe(
        catchError(e => {
          this.loading = false;
          this.notificationService.error({
            title: 'SIP config',
            message: 'An error occurred while saving the config',
            serviceName: 'SIP',
            requestMessage: e.statusText,
            requestCode: e.status,
          });
          config.pingEnabled = !config.pingEnabled;
          return throwError(() => e);
        })
      )
      .subscribe(() => {
        this.loading = false;
        this.notificationService.success('The SIP OPTIONS ping setting has been successfully updated.', 'SIP Config Saved');
        this.update();
      });
  }

  formatDate(date) {
    return this.datePipe.transform(date, 'dd MMM HH:mm:ss');
  }

  resetBatch() {
    this.batchSelectedIds.clear();
    this.gridApi.deselectAll();
  }

  // ng grid ...
  onGridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.update();
    const columnState = this.localStorage.get(`sip_config_table_state_${this.userService.authUser.id}`);
    this.gridApi.applyColumnState({ state: columnState, applyOrder: true });
  }

  onRowSelected(event: RowSelectedEvent) {
    const selectedRows = this.gridApi.getSelectedRows();
    this.batchSelectedIds = new Set(selectedRows.map(row => row.id));
    this.changeBatch.emit(selectedRows);
  }
  // ng grid ...
}
